import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormInput from '../../shared/components/ui/FormInput';
import NavigationSelector from '../../shared/components/ui/NavigationSelector';
import { Pencil, Trash, Copy } from 'lucide-react';
import { getAllCampaigns } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../shared/components/ui/Loader';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const CampaignsList = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [isloading, setisLoading] = useState(false);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const validationSchema = yup.object().shape({
    project: yup.string().required('Select Project')
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const projectID = watch('project');

  const {
    data: AllCampaign,
    refetch: fetchCampaigns,
    isLoading: isCampaignLoading
  } = useQuery({
    queryKey: ['getAllCampaigns', projectID],
    queryFn: () => getAllCampaigns(projectID),
    enabled: !!projectID
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Campaigns List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (projectID) fetchCampaigns();
  }, [projectID]);

  useEffect(() => {
    setisLoading(isCampaignLoading);
  }, [isCampaignLoading]);

  useEffect(() => {
    if (projects?.length > 0 && !projectID) {
      setCurrentProjectIndex(0);
      setValue('project', projects[0].id);
    }
  }, [projects, projectID, setValue]);

  const formatDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }); // May, Jun, etc.
    const year = date.getFullYear();

    // Add "st", "nd", "rd", "th"
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th'; // 11th, 12th, 13th
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const handleProjectSelection = useCallback(
    (project, index) => {
      setCurrentProjectIndex(index);
      setValue('project', project.id);
    },
    [setValue]
  );

  return (
    <>
      <Loader isVisible={isloading || isProjectsLoading} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header backArrow={false} data={{ name: 'Campaign Listings' }} />
        </div>
        <div className="mt-[5rem]">
          <NavigationSelector
            data={projects || []}
            onSelectionChange={handleProjectSelection}
            initialIndex={currentProjectIndex}
            labelKey="name"
            valueKey="id"
            placeholder="Loading projects..."
            className="w-max ml-[1.65rem]"
          />

          <div className="pt-[1rem] pl-4 pr-4">
            <div className="shadow overflow-auto rounded-lg h-[calc(100vh-10rem)]">
              {AllCampaign && AllCampaign.length > 0 ? (
                <table className="min-w-full rounded-t-xl text-left">
                  <thead className=" bg-gray-200 !text-gray-600 sticky top-0 z-20">
                    <tr className="font-normal h-[3rem]">
                      <th className="px-3 py-2 border-b font-normal ">Campaign Name</th>
                      <th className="px-3 py-2 border-b font-normal">Channel</th>
                      <th className="px-3 py-2 border-b font-normal">Start Date</th>
                      <th className="px-3 py-2 border-b font-normal">End Date</th>
                      <th className="px-3 py-2 border-b font-normal">Created by</th>
                      <th className="px-3 py-2 border-b font-normal">Landing Page URL</th>
                      <th className="px-3 py-2 border-b font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AllCampaign.map((campaign, index) => {
                      return (
                        <React.Fragment key={campaign.id}>
                          <tr
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem] cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/builder/marketing/forms/${campaign.name}/${campaign.id}/${projectID}`
                              )
                            }>
                            <td className="px-4 py-2">{campaign.name}</td>
                            <td className="px-4 py-2">{campaign.channel || '--'}</td>
                            <td className="px-4 py-2">{formatDate(campaign.start_date) || '--'}</td>
                            <td className="px-4 py-2">{formatDate(campaign.end_date) || '--'}</td>
                            <td className="px-4 py-2">{campaign.user?.name || '--'}</td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {campaign?.project?.name && campaign?.id && (
                                  <>
                                    {(() => {
                                      const domain = window.location.origin;
                                      const projectName = campaign.project?.name.replace(
                                        /\s+/g,
                                        '_'
                                      );
                                      const url = `${domain}/${projectName}/${campaign.id}`;

                                      return (
                                        <>
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="truncate max-w-[150px] text-blue-600 hover:underline"
                                            onClick={(e) => e.stopPropagation()}>
                                            {url}
                                          </a>
                                          <Copy
                                            className="w-4 h-4 cursor-pointer hover:text-blue-500"
                                            onClick={(e) => {
                                              handleCopy(url);
                                              e.stopPropagation();
                                            }}
                                            title="Copy URL"
                                          />
                                        </>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 flex space-x-2 items-center text-gray-600">
                              <Pencil
                                className="w-4 h-4 cursor-pointer hover:text-blue-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/builder/marketing/edit/campaign/${campaign.id}`);
                                }}
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex justify-center items-center h-[30rem] text-gray-600">
                  <img
                    src={noDataIcon}
                    alt="No Data"
                    className="w-[20rem] h-[15rem] object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default CampaignsList;
